import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import PolygonMap from './PolygonMap.jsx';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import './harbor-geography.scss';

const EMPTY_PORT = { id: null, name: '', code: '', boundary: [] };

export default function HarborGeographyPage() {
  const [ports, setPorts] = useState([]);
  const [stations, setStations] = useState([]);
  const [berths, setBerths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Port editor.
  const [port, setPort] = useState(EMPTY_PORT);
  const [boundaryText, setBoundaryText] = useState('[]');
  const [portErrors, setPortErrors] = useState({});
  const [portMsg, setPortMsg] = useState(null);
  const [savingPort, setSavingPort] = useState(false);

  // Page-level feedback for berth/station actions, plus the pending delete
  // ({ kind: 'port' | 'berth' | 'station', id, name }) awaiting confirmation.
  const [geoMsg, setGeoMsg] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Berth + station forms.
  const [berthForm, setBerthForm] = useState({ name: '', lengthM: '', draftM: '' });
  const [stationForm, setStationForm] = useState({ name: '', latitude: '', longitude: '' });

  const loadPorts = useCallback(async () => {
    const [portsRes, stationsRes] = await Promise.all([api.listPorts(), api.listStations()]);
    setPorts(portsRes || []);
    setStations(stationsRes || []);
    return portsRes || [];
  }, []);

  const loadBerths = useCallback(async (portId) => {
    if (!portId) {
      setBerths([]);
      return;
    }
    const res = await api.listBerths(portId);
    setBerths(res || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await loadPorts();
      } catch (err) {
        setLoadError(err.message || 'Failed to load harbor geography.');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadPorts]);

  // Keep the boundary text and the parsed array in sync from map edits.
  const setBoundaryFromMap = (next) => {
    setPort((p) => ({ ...p, boundary: next }));
    setBoundaryText(JSON.stringify(next));
  };

  const onBoundaryTextChange = (value) => {
    setBoundaryText(value);
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        setPort((p) => ({ ...p, boundary: parsed }));
        setPortErrors((e) => ({ ...e, boundary: undefined }));
      }
    } catch {
      setPortErrors((e) => ({ ...e, boundary: 'Coordinates must be valid JSON, e.g. [[lat,lng],…]' }));
    }
  };

  const selectPort = async (p) => {
    setPortMsg(null);
    setPortErrors({});
    setPort({ id: p.id, name: p.name, code: p.code || '', boundary: p.boundary || [] });
    setBoundaryText(JSON.stringify(p.boundary || []));
    await loadBerths(p.id);
  };

  const newPort = () => {
    setPortMsg(null);
    setPortErrors({});
    setPort(EMPTY_PORT);
    setBoundaryText('[]');
    setBerths([]);
  };

  const validatePort = () => {
    const next = {};
    if (!port.name.trim()) next.name = 'Port name is required';
    let boundary;
    try {
      boundary = JSON.parse(boundaryText);
      if (!Array.isArray(boundary)) throw new Error('not array');
      const ok = boundary.every(
        (pt) => Array.isArray(pt) && pt.length === 2 && pt.every((n) => typeof n === 'number')
      );
      if (!ok) next.boundary = 'Each vertex must be [lat, lng]';
      else if (boundary.length > 0 && boundary.length < 3)
        next.boundary = 'A polygon needs at least 3 vertices';
    } catch {
      next.boundary = 'Coordinates must be valid JSON';
    }
    setPortErrors(next);
    return Object.keys(next).length === 0 ? boundary : null;
  };

  const savePort = async (e) => {
    e.preventDefault();
    setPortMsg(null);
    const boundary = validatePort();
    if (boundary === null) return;
    setSavingPort(true);
    try {
      const payload = { name: port.name.trim(), code: port.code.trim() || undefined, boundary };
      const saved = port.id ? await api.updatePort(port.id, payload) : await api.createPort(payload);
      setPortMsg({ type: 'success', text: 'Port geofence saved successfully.' });
      const list = await loadPorts();
      const fresh = list.find((p) => p.id === (saved?.id || port.id)) || saved;
      if (fresh) await selectPortQuiet(fresh);
    } catch (err) {
      setPortMsg({ type: 'danger', text: err.message || 'Failed to save port.' });
    } finally {
      setSavingPort(false);
    }
  };

  // Re-select without resetting the success message.
  const selectPortQuiet = async (p) => {
    setPort({ id: p.id, name: p.name, code: p.code || '', boundary: p.boundary || [] });
    setBoundaryText(JSON.stringify(p.boundary || []));
    await loadBerths(p.id);
  };

  const deletePort = async () => {
    if (!port.id) return;
    setPortMsg(null);
    setGeoMsg(null);
    setSavingPort(true);
    try {
      await api.deletePort(port.id);
      await loadPorts();
      newPort();
      setPortMsg({ type: 'success', text: 'Port deleted.' });
    } catch (err) {
      setPortMsg({ type: 'danger', text: err.message || 'Failed to delete port.' });
    } finally {
      setSavingPort(false);
    }
  };

  const addBerth = async (e) => {
    e.preventDefault();
    if (!port.id || !berthForm.name.trim()) return;
    setGeoMsg(null);
    const name = berthForm.name.trim();
    try {
      await api.createBerth(port.id, {
        name,
        lengthM: berthForm.lengthM ? Number(berthForm.lengthM) : undefined,
        draftM: berthForm.draftM ? Number(berthForm.draftM) : undefined,
      });
      setBerthForm({ name: '', lengthM: '', draftM: '' });
      setGeoMsg({ type: 'success', text: `Berth ${name} added.` });
      await loadBerths(port.id);
    } catch (err) {
      setGeoMsg({ type: 'danger', text: err.message || 'Failed to add berth.' });
    }
  };

  const removeBerth = async (id) => {
    setGeoMsg(null);
    try {
      await api.deleteBerth(id);
      setGeoMsg({ type: 'success', text: 'Berth deleted.' });
      await loadBerths(port.id);
    } catch (err) {
      setGeoMsg({ type: 'danger', text: err.message || 'Failed to delete berth.' });
    }
  };

  const addStation = async (e) => {
    e.preventDefault();
    if (!stationForm.name.trim()) return;
    setGeoMsg(null);
    const name = stationForm.name.trim();
    try {
      await api.createStation({
        name,
        latitude: Number(stationForm.latitude),
        longitude: Number(stationForm.longitude),
      });
      setStationForm({ name: '', latitude: '', longitude: '' });
      setGeoMsg({ type: 'success', text: `Station ${name} added.` });
      await loadPorts();
    } catch (err) {
      setGeoMsg({ type: 'danger', text: err.message || 'Failed to add station.' });
    }
  };

  const removeStation = async (id) => {
    setGeoMsg(null);
    try {
      await api.deleteStation(id);
      setGeoMsg({ type: 'success', text: 'Station deleted.' });
      await loadPorts();
    } catch (err) {
      setGeoMsg({ type: 'danger', text: err.message || 'Failed to delete station.' });
    }
  };

  const confirmDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      if (confirmDel.kind === 'port') await deletePort();
      else if (confirmDel.kind === 'berth') await removeBerth(confirmDel.id);
      else await removeStation(confirmDel.id);
    } finally {
      setDeleting(false);
      setConfirmDel(null);
    }
  };

  const confirmText = !confirmDel
    ? ''
    : confirmDel.kind === 'port'
      ? `Delete port ${confirmDel.name}? Its berths and geofence will be removed.`
      : confirmDel.kind === 'berth'
        ? `Delete berth ${confirmDel.name}? Bookings/port calls referencing it will be unlinked.`
        : `Delete station ${confirmDel.name}? Tugs homed here will lose their home station.`;

  return (
    <div className="container-fluid py-4" data-testid="geography-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-map-marked-alt me-2" aria-hidden="true" />
                Harbor Geography &amp; Station Master
              </h3>
              <p className="text-muted mb-0 small">
                Maintain ports, berths, and tug base stations — define port geofence boundaries on the map.
              </p>
            </div>
            <span className="badge bg-primary p-2">Master Data</span>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="geography-load-error">
          {loadError}
        </div>
      )}
      {geoMsg && (
        <div className={`alert alert-${geoMsg.type}`} role="alert" data-testid="geo-message">
          {geoMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* LEFT: map + port editor */}
          <div className="col-lg-8 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-draw-polygon me-2" aria-hidden="true" />
                  {port.id ? 'Edit Port Geofence' : 'New Port Geofence'}
                </h5>
                <button className="btn btn-sm btn-outline-primary" onClick={newPort} data-testid="new-port">
                  <i className="fas fa-plus me-1" aria-hidden="true" />
                  New Port
                </button>
              </div>
              <div className="card-body">
                {portMsg && (
                  <div className={`alert alert-${portMsg.type}`} role="alert" data-testid="port-message">
                    {portMsg.text}
                  </div>
                )}

                <PolygonMap boundary={port.boundary} onChange={setBoundaryFromMap} />

                <form onSubmit={savePort} className="mt-3" noValidate>
                  <div className="row g-3">
                    <div className="col-md-8">
                      <label htmlFor="port-name" className="form-label small fw-bold text-muted">
                        Port Name
                      </label>
                      <input
                        id="port-name"
                        type="text"
                        className={`form-control form-control-sm${portErrors.name ? ' is-invalid' : ''}`}
                        value={port.name}
                        onChange={(e) => setPort({ ...port, name: e.target.value })}
                        data-testid="port-name"
                      />
                      {portErrors.name && <div className="invalid-feedback">{portErrors.name}</div>}
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="port-code" className="form-label small fw-bold text-muted">
                        Code
                      </label>
                      <input
                        id="port-code"
                        type="text"
                        className="form-control form-control-sm"
                        value={port.code}
                        onChange={(e) => setPort({ ...port, code: e.target.value })}
                        data-testid="port-code"
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="port-boundary" className="form-label small fw-bold text-muted">
                        Geographical Boundary Coordinates (Polygon) — [[lat, lng], …]
                      </label>
                      <textarea
                        id="port-boundary"
                        rows={3}
                        className={`form-control form-control-sm font-monospace${portErrors.boundary ? ' is-invalid' : ''}`}
                        value={boundaryText}
                        onChange={(e) => onBoundaryTextChange(e.target.value)}
                        data-testid="port-boundary"
                      />
                      {portErrors.boundary && <div className="invalid-feedback">{portErrors.boundary}</div>}
                      <div className="form-text">
                        Click the map to add vertices, or drag a vertex to adjust the geofence.
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-3">
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={savingPort}
                      data-testid="save-port"
                    >
                      {savingPort && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-save me-1" aria-hidden="true" />
                      Save
                    </button>
                    {port.id && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => setConfirmDel({ kind: 'port', id: port.id, name: port.name })}
                        disabled={savingPort}
                        data-testid="delete-port"
                      >
                        <i className="fas fa-trash me-1" aria-hidden="true" />
                        Delete
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Berths for the selected port */}
            {port.id && (
              <div className="card border-0 shadow-sm mt-4">
                <div className="card-header bg-white pt-4 border-bottom-0">
                  <h5 className="mb-0 text-secondary">
                    <i className="fas fa-anchor me-2" aria-hidden="true" />
                    Berths — {port.name}
                  </h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead className="text-secondary small text-uppercase">
                        <tr>
                          <th>Berth</th>
                          <th>Max LOA (m)</th>
                          <th>Max Draft (m)</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {berths.length === 0 && (
                          <tr>
                            <td colSpan={4} className="text-muted small text-center py-3">
                              No berths yet.
                            </td>
                          </tr>
                        )}
                        {berths.map((b) => (
                          <tr key={b.id} data-testid="berth-row" data-name={b.name}>
                            <td className="fw-bold">{b.name}</td>
                            <td>{b.length_m ?? '—'}</td>
                            <td>{b.draft_m ?? '—'}</td>
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => setConfirmDel({ kind: 'berth', id: b.id, name: b.name })}
                                aria-label={`Delete ${b.name}`}
                                data-testid="berth-delete"
                              >
                                <i className="fas fa-trash" aria-hidden="true" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <form className="row g-2 align-items-end" onSubmit={addBerth}>
                    <div className="col-md-5">
                      <label htmlFor="berth-name" className="form-label small text-muted mb-0">
                        Berth name
                      </label>
                      <input
                        id="berth-name"
                        className="form-control form-control-sm"
                        value={berthForm.name}
                        onChange={(e) => setBerthForm({ ...berthForm, name: e.target.value })}
                        data-testid="berth-name"
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="berth-length" className="form-label small text-muted mb-0">
                        Max LOA (m)
                      </label>
                      <input
                        id="berth-length"
                        type="number"
                        step="0.1"
                        className="form-control form-control-sm"
                        value={berthForm.lengthM}
                        onChange={(e) => setBerthForm({ ...berthForm, lengthM: e.target.value })}
                        data-testid="berth-length"
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="berth-draft" className="form-label small text-muted mb-0">
                        Max Draft (m)
                      </label>
                      <input
                        id="berth-draft"
                        type="number"
                        step="0.1"
                        className="form-control form-control-sm"
                        value={berthForm.draftM}
                        onChange={(e) => setBerthForm({ ...berthForm, draftM: e.target.value })}
                        data-testid="berth-draft"
                      />
                    </div>
                    <div className="col-md-1">
                      <button className="btn btn-primary btn-sm w-100" data-testid="add-berth" aria-label="Add berth">
                        <i className="fas fa-plus" aria-hidden="true" />
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: ports list + stations */}
          <div className="col-lg-4 mb-4">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-water me-2" aria-hidden="true" />
                  Ports
                </h5>
              </div>
              <div className="list-group list-group-flush">
                {ports.length === 0 && <div className="list-group-item text-muted small">No ports yet.</div>}
                {ports.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center${
                      p.id === port.id ? ' active' : ''
                    }`}
                    onClick={() => selectPort(p)}
                    data-testid="port-row"
                    data-name={p.name}
                  >
                    <span>
                      <i className="fas fa-map-pin me-2" aria-hidden="true" />
                      {p.name}
                      {p.code ? <span className="text-muted small ms-1">({p.code})</span> : null}
                    </span>
                    <span className="badge bg-light text-dark border">{(p.boundary || []).length} pts</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-broadcast-tower me-2" aria-hidden="true" />
                  Tug Base Stations
                </h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead className="text-secondary small text-uppercase">
                      <tr>
                        <th>Station</th>
                        <th>Lat</th>
                        <th>Lng</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {stations.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-muted small text-center py-3">
                            No stations yet.
                          </td>
                        </tr>
                      )}
                      {stations.map((s) => (
                        <tr key={s.id} data-testid="station-row" data-name={s.name}>
                          <td className="fw-bold">{s.name}</td>
                          <td>{s.latitude}</td>
                          <td>{s.longitude}</td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setConfirmDel({ kind: 'station', id: s.id, name: s.name })}
                              aria-label={`Delete ${s.name}`}
                              data-testid="station-delete"
                            >
                              <i className="fas fa-trash" aria-hidden="true" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <form className="row g-2 align-items-end" onSubmit={addStation}>
                  <div className="col-12">
                    <label htmlFor="station-name" className="form-label small text-muted mb-0">
                      Station name
                    </label>
                    <input
                      id="station-name"
                      className="form-control form-control-sm"
                      value={stationForm.name}
                      onChange={(e) => setStationForm({ ...stationForm, name: e.target.value })}
                      data-testid="station-name"
                    />
                  </div>
                  <div className="col-6">
                    <label htmlFor="station-lat" className="form-label small text-muted mb-0">
                      Latitude
                    </label>
                    <input
                      id="station-lat"
                      type="number"
                      step="0.000001"
                      className="form-control form-control-sm"
                      value={stationForm.latitude}
                      onChange={(e) => setStationForm({ ...stationForm, latitude: e.target.value })}
                      data-testid="station-lat"
                    />
                  </div>
                  <div className="col-6">
                    <label htmlFor="station-lng" className="form-label small text-muted mb-0">
                      Longitude
                    </label>
                    <input
                      id="station-lng"
                      type="number"
                      step="0.000001"
                      className="form-control form-control-sm"
                      value={stationForm.longitude}
                      onChange={(e) => setStationForm({ ...stationForm, longitude: e.target.value })}
                      data-testid="station-lng"
                    />
                  </div>
                  <div className="col-12">
                    <button className="btn btn-primary btn-sm w-100" data-testid="add-station">
                      <i className="fas fa-plus me-1" aria-hidden="true" />
                      Add Station
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmDel}
        title={`Delete ${confirmDel?.kind || ''}`}
        message={confirmText}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDel(null)}
      />
    </div>
  );
}
